import { AlertCircle, CheckCircle, Info, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { TYPOGRAPHY } from '@/design-system';
import { cn } from "@/lib/utils";
import { ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export type FieldStatus = 'idle' | 'validating' | 'saving' | 'saved' | 'error';

interface FormFieldErrorProps {
  error?: string;
  className?: string;
}

export const FormFieldError = ({ error, className }: FormFieldErrorProps) => {
  if (!error) return null;

  return (
    <div className={cn("flex items-center gap-1.5 mt-1.5", className)}>
      <AlertCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
      <p className="text-xs text-destructive">{error}</p>
    </div>
  );
};

interface FormFieldSuccessProps {
  message?: string;
  className?: string;
}

export const FormFieldSuccess = ({ message, className }: FormFieldSuccessProps) => {
  if (!message) return null;

  return (
    <div className={cn("flex items-center gap-1.5 mt-1.5", className)}>
      <CheckCircle className="h-3.5 w-3.5 text-success flex-shrink-0" />
      <p className="text-xs text-success">{message}</p>
    </div>
  );
};

interface FormFieldInfoProps {
  message?: string;
  className?: string;
}

export const FormFieldInfo = ({ message, className }: FormFieldInfoProps) => {
  if (!message) return null;

  return (
    <div className={cn("flex items-center gap-1.5 mt-1.5", className)}>
      <Info className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      <p className={cn(TYPOGRAPHY.caption)}>{message}</p>
    </div>
  );
};

interface FieldStatusIndicatorProps {
  status: FieldStatus;
  className?: string;
}

export const FieldStatusIndicator = ({ status, className }: FieldStatusIndicatorProps) => {
  if (status === 'idle') return null;

  return (
    <div className={cn("absolute right-3 top-1/2 -translate-y-1/2", className)}>
      {status === 'validating' && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      )}
      {status === 'saving' && (
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
      )}
      {status === 'saved' && (
        <CheckCircle2 className="h-4 w-4 text-success" />
      )}
      {status === 'error' && (
        <XCircle className="h-4 w-4 text-destructive" />
      )}
    </div>
  );
};

interface ValidatedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  success?: string;
  info?: string;
  fieldStatus?: FieldStatus;
}

export const ValidatedInput = ({ error, success, info, fieldStatus = 'idle', className, ...props }: ValidatedInputProps) => {
  const hasError = !!error;
  const hasSuccess = !!success && !error;
  const showStatusIndicator = fieldStatus !== 'idle';

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Input
          error={hasError}
          success={hasSuccess}
          className={cn(showStatusIndicator && "pr-10", className)}
          {...props}
        />
        {showStatusIndicator && <FieldStatusIndicator status={fieldStatus} />}
      </div>
      <FormFieldError error={error} />
      <FormFieldSuccess message={success} />
      <FormFieldInfo message={info} />
    </div>
  );
};

interface ValidatedTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
  success?: string;
  info?: string;
  fieldStatus?: FieldStatus;
}

export const ValidatedTextarea = ({ error, success, info, fieldStatus = 'idle', className, ...props }: ValidatedTextareaProps) => {
  const hasError = !!error;
  const hasSuccess = !!success && !error;
  const showStatusIndicator = fieldStatus !== 'idle';

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Textarea
          error={hasError}
          success={hasSuccess}
          className={cn(showStatusIndicator && "pr-10", className)}
          {...props}
        />
        {showStatusIndicator && (
          <FieldStatusIndicator 
            status={fieldStatus} 
            className="top-3 -translate-y-0" 
          />
        )}
      </div>
      <FormFieldError error={error} />
      <FormFieldSuccess message={success} />
      <FormFieldInfo message={info} />
    </div>
  );
};

interface ValidatedSelectWrapperProps {
  error?: string;
  success?: string;
  info?: string;
  fieldStatus?: FieldStatus;
  children: ReactNode;
  className?: string;
}

export const ValidatedSelectWrapper = ({ error, success, info, fieldStatus = 'idle', children, className }: ValidatedSelectWrapperProps) => {
  const showStatusIndicator = fieldStatus !== 'idle';
  
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="relative">
        {children}
        {showStatusIndicator && (
          <FieldStatusIndicator 
            status={fieldStatus} 
            className="right-10" 
          />
        )}
      </div>
      <FormFieldError error={error} />
      <FormFieldSuccess message={success} />
      <FormFieldInfo message={info} />
    </div>
  );
};
